<!-- HotX Console 0.1.135；实际修改时间：2026-10-05 23:51:38 CST；精确父基线：5b6418f92c63263096a53308363f8963c241186a；仅底部节点标签增加第二行 IP。 -->
<!-- HotX Console 0.1.134 / TRAFFIC-NODE-ORDER-20261005；实际修改时间：2026-10-05 23:34:32 CST；精确父基线：eec5e10d89d6321b649faf98395087f2ac646c26；仅标签和明细按当前桶带宽降序。 -->
<!-- HotX Console 0.1.133 / TRAFFIC-NODE-ONLY-20261005；实际修改时间：2026-10-05 23:08:00 CST；精确父基线：92b8cc70b43cc89e503e4fc5aea0a4a473eef78f；仅节点绘图，保留总量数值。 -->
<!-- HotX Console 0.1.132；实际修改时间：2026-10-04 13:33:00 CST；精确父基线：a49fc55510200cda1140422277e53de5c0cd6fa5；变更：TRAFFIC-LINE-LEGEND-B-20261004，仅展示样式。 -->
<!-- HotX Console 0.1.128；实际修改时间：2026-10-01 12:08:00 CST；精确父基线：8134c31a7bebab26fc7880ade220dc0bb8bfe694；变更：TRAFFIC-TIME-LOCALE-ZH-20261001。 -->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type {
  NodeAnalyticsCoverage,
  NodeTrafficPoint,
  NodeTrafficSeries,
  TrafficPoint
} from '../../api/types';

type TimeRange = 'today' | '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';
type Granularity = '5m' | '1h' | '1d';

interface Props {
  totalPoints: TrafficPoint[];
  nodeSeries: NodeTrafficSeries[];
  coverage: NodeAnalyticsCoverage | null;
  timeRange: TimeRange;
  granularity: Granularity;
  minimumScaleMaxBps?: number;
  rangeStart?: string;
  rangeEnd?: string;
  realtime?: boolean;
}

interface SeriesStyle {
  color: string;
  dash: string;
}

interface DetailRow {
  key: string;
  label: string;
  value: number | null;
  color: string;
  dash: string;
}

interface PlotSample {
  bucket: string;
  cadenceSeconds: number;
  value: number | null;
}

interface PlotPoint extends PlotSample {
  x: number;
  y: number;
}

const props = withDefaults(defineProps<Props>(), {
  minimumScaleMaxBps: 0,
  rangeStart: '',
  rangeEnd: '',
  realtime: true
});
const emit = defineEmits<{
  pan: [direction: -1 | 1];
  'return-realtime': [];
}>();

const CHART = { width: 1_000, height: 278, left: 72, right: 24, top: 24, bottom: 44 } as const;
const TOTAL_COLOR = '#12734e';
const NODE_COLORS = ['#2563eb', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4f46e5', '#0d9488'];
const NODE_DASHES = ['10 5', '4 4', '12 4 2 4', '16 5', '6 3 2 3', '12 3 2 3 2 3', '2 5'];
// Assign from all returned nodes before coverage/visibility filtering, so toggling a line
// or losing a sample cannot change another node's legend. Keep existing node colors.
const nodeDashById = computed(() => new Map(
  [...props.nodeSeries].sort(compareNodes).map((node, index) => [node.nodeId, NODE_DASHES[index % NODE_DASHES.length]])
));
const naturalNodeOrder = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' });
const hiddenNodeIds = ref<Set<number>>(new Set());
const activeBucket = ref<string | null>(null);
const navigatorStart = ref(0);
const navigatorEnd = ref(100);

const effectiveNodeSeries = computed(() =>
  props.nodeSeries
    .map(node => ({ ...node, points: node.points.filter(isCoveredNodePoint) }))
    .filter(node => node.points.some(point => nodePointBandwidth(point) !== null))
    .slice()
    .sort(compareNodes)
);

const visibleNodeSeries = computed(() =>
  effectiveNodeSeries.value.filter(node => !hiddenNodeIds.value.has(node.nodeId))
);

const totalPointByBucket = computed(() => new Map(
  props.totalPoints
    .filter(point => Boolean(point.bucketStart))
    .map(point => [normalizeBucket(point.bucketStart as string), point] as const)
));

const allNodePointByBucket = computed(() => new Map(
  props.nodeSeries.map(node => [
    node.nodeId,
    new Map(node.points
      .filter(isCoveredNodePoint)
      .map(point => [normalizeBucket(point.bucketStart), point] as const))
  ] as const)
));

const nodePointByBucket = computed(() => new Map(
  effectiveNodeSeries.value.map(node => [node.nodeId, allNodePointByBucket.value.get(node.nodeId) ?? new Map()] as const)
));

const fullTimeline = computed(() => {
  const buckets = new Set<string>();
  for (const point of props.totalPoints) {
    if (point.bucketStart) buckets.add(normalizeBucket(point.bucketStart));
  }
  for (const node of effectiveNodeSeries.value) {
    for (const point of node.points) {
      if (point.bucketStart) buckets.add(normalizeBucket(point.bucketStart));
    }
  }
  return [...buckets].sort(compareBuckets);
});

const timeline = computed(() => {
  const buckets = fullTimeline.value;
  if (buckets.length < 2) return buckets;
  const start = Math.floor((navigatorStart.value / 100) * (buckets.length - 1));
  const end = Math.ceil((navigatorEnd.value / 100) * (buckets.length - 1));
  return buckets.slice(start, Math.max(start + 1, end + 1));
});

const timelineIndex = computed(() => new Map(timeline.value.map((bucket, index) => [bucket, index] as const)));

const totalPlotSamples = computed<PlotSample[]>(() => props.totalPoints
  .filter((point): point is TrafficPoint & { bucketStart: string } => Boolean(point.bucketStart))
  .map(point => ({
    bucket: normalizeBucket(point.bucketStart),
    cadenceSeconds: totalPointBucketSeconds(point),
    value: totalPointBandwidth(point)
  }))
  .sort((left, right) => compareBuckets(left.bucket, right.bucket))
);

const nodePlotSamples = computed(() => new Map(effectiveNodeSeries.value.map(node => [
  node.nodeId,
  node.points
    .filter(point => Boolean(point.bucketStart))
    .map(point => ({
      bucket: normalizeBucket(point.bucketStart),
      cadenceSeconds: point.bucketSeconds,
      value: nodePointBandwidth(point)
    }))
    .sort((left, right) => compareBuckets(left.bucket, right.bucket))
] as const)));

const totalValues = computed(() => timeline.value.map(bucket => {
  const point = totalPointByBucket.value.get(bucket);
  return point ? totalPointBandwidth(point) : null;
}));

const visibleNodeValues = computed(() => new Map(
  visibleNodeSeries.value.map(node => [
    node.nodeId,
    timeline.value.map(bucket => {
      const point = nodePointByBucket.value.get(node.nodeId)?.get(bucket);
      return point ? nodePointBandwidth(point) : null;
    })
  ] as const)
));

const scaleMaxBps = computed(() => {
  const visibleValues = [
    ...[...visibleNodeValues.value.values()].flat()
  ].filter((value): value is number => value !== null);
  const peak = visibleValues.length ? Math.max(...visibleValues) : 0;
  const minimum = finiteNonNegative(props.minimumScaleMaxBps) ?? 0;
  return niceCeiling(Math.max(peak, minimum));
});

const yAxisTicks = computed(() => [1, 0.75, 0.5, 0.25, 0].map(ratio => ({
  ratio,
  y: CHART.top + (1 - ratio) * plotHeight(),
  label: formatBandwidth(scaleMaxBps.value * ratio)
})));
const xAxisTicks = computed(() => {
  if (!timeline.value.length) return [];
  const indexes = new Set<number>();
  const count = Math.min(6, timeline.value.length);
  for (let index = 0; index < count; index += 1) {
    indexes.add(Math.round((index / Math.max(1, count - 1)) * (timeline.value.length - 1)));
  }
  return [...indexes].map(index => ({
    bucket: timeline.value[index],
    x: xAt(index),
    label: bucketLabel(timeline.value[index]),
    transform: index === 0
      ? 'translateX(0%)'
      : index === timeline.value.length - 1
        ? 'translateX(-100%)'
        : 'translateX(-50%)'
  }));
});
const activeGuideX = computed(() => {
  if (!activeBucket.value) return null;
  const index = timeline.value.indexOf(activeBucket.value);
  return index < 0 ? null : xAt(index);
});
const activeGuideY = computed(() => {
  if (!activeBucket.value) return null;
  const index = timeline.value.indexOf(activeBucket.value);
  const value = index < 0 ? null : totalValues.value[index];
  return value === null || value === undefined ? null : yAt(value);
});
const activeTooltipStyle = computed(() => ({
  left: `clamp(min(160px, 50%), ${((activeGuideX.value ?? CHART.left) / CHART.width) * 100}%, max(50%, calc(100% - 160px)))`,
  top: `${Math.min(72, Math.max(15, ((activeGuideY.value ?? CHART.top) / CHART.height) * 100))}%`
}));
const peakPoint = computed(() => totalPlotSamples.value
  .filter((point): point is PlotSample & { value: number } => point.value !== null && timelineIndex.value.has(point.bucket))
  .reduce<(PlotSample & { value: number }) | null>((peak, point) => !peak || point.value > peak.value ? point : peak, null));
const currentPoint = computed(() => {
  const selected = activeBucket.value
    ? totalPlotSamples.value.find(point => point.bucket === activeBucket.value && point.value !== null)
    : null;
  return selected ?? [...totalPlotSamples.value].reverse().find(point => point.value !== null && timelineIndex.value.has(point.bucket)) ?? null;
});
const navigatorSelectionStyle = computed(() => ({
  left: `${navigatorStart.value}%`,
  width: `${navigatorEnd.value - navigatorStart.value}%`
}));
// Overview uses the full common timeline, with the same node gaps and styles as the main plot.
const navigatorMaximum = computed(() => Math.max(1, ...visibleNodeSeries.value.flatMap(node =>
  (nodePlotSamples.value.get(node.nodeId) ?? []).map(point => point.value ?? 0)
)));
function navigatorNodePath(nodeId: number) {
  return plotSegments(nodePlotSamples.value.get(nodeId) ?? [], true)
    .map(segment => smoothPointPath(segment)).join(' ');
}
const navigatorTicks = computed(() => {
  if (!fullTimeline.value.length) return [];
  return [fullTimeline.value[0], fullTimeline.value[Math.floor((fullTimeline.value.length - 1) / 2)], fullTimeline.value.at(-1) as string];
});

const coverageText = computed(() => {
  if (props.coverage?.status === 'PARTIAL') {
    const boundary = props.coverage.completeFrom
      ? bucketLabel(normalizeBucket(props.coverage.completeFrom))
      : '';
    return boundary
      ? `节点数据从 ${boundary} 起参与同桶对账；此前不完整节点数据已断线显示。`
      : '节点数据仅覆盖部分时间，不完整节点值不作为总量对账依据。';
  }
  if (props.coverage?.status === 'UNAVAILABLE') {
    return '所选时间范围暂无节点维度数据，总带宽仍按原统计展示。';
  }
  return '';
});

// Sort presentation only; retain stable plot order and nodeId-based colors/dashes.
const rankedNodeSeries = computed(() => {
  const bandwidth = (node: NodeTrafficSeries) => {
    const point = activeBucket.value ? nodePointByBucket.value.get(node.nodeId)?.get(activeBucket.value) : null;
    return point ? nodePointBandwidth(point) : null;
  };
  return [...effectiveNodeSeries.value].sort((left, right) => {
    const leftValue = bandwidth(left);
    const rightValue = bandwidth(right);
    if (leftValue === null && rightValue !== null) return 1;
    if (leftValue !== null && rightValue === null) return -1;
    return (rightValue ?? 0) - (leftValue ?? 0) || compareNodes(left, right);
  });
});

const activeDetailRows = computed<DetailRow[]>(() => {
  if (!activeBucket.value) return [];
  const bucketIndex = timeline.value.indexOf(activeBucket.value);
  if (bucketIndex < 0) return [];
  return [
    {
      key: 'total',
      label: '总带宽',
      value: totalValues.value[bucketIndex] ?? null,
      color: TOTAL_COLOR,
      dash: ''
    },
    ...rankedNodeSeries.value.filter(node => !hiddenNodeIds.value.has(node.nodeId)).map(node => ({
      key: `node-${node.nodeId}`,
      label: `${nodeCode(node)} · ${nodeName(node)}`,
      value: visibleNodeValues.value.get(node.nodeId)?.[bucketIndex] ?? null,
      ...nodeStyle(node.nodeId)
    }))
  ];
});

const activeReconciliation = computed(() => {
  if (!activeBucket.value) return null;
  const bucketIndex = timeline.value.indexOf(activeBucket.value);
  if (bucketIndex < 0) return null;
  const total = totalValues.value[bucketIndex] ?? null;
  const nodeTotal = nodeTotalAt(bucketIndex);
  if (!nodeTotal.complete) {
    return { state: 'unavailable', text: '节点数据不完整，无法与总带宽对账' };
  }
  if (total === null || nodeTotal.value === null) {
    return { state: 'unavailable', text: '当前时间桶暂无完整节点数据' };
  }
  const tolerance = Math.max(1, Math.abs(total) * 0.001);
  const matches = Math.abs(total - nodeTotal.value) <= tolerance;
  return matches
    ? { state: 'match', text: '节点数据与总带宽一致' }
    : { state: 'mismatch', text: '节点数据与总带宽不一致，请检查采集完整性' };
});

watch(timeline, buckets => {
  if (!activeBucket.value || !buckets.includes(activeBucket.value)) activeBucket.value = buckets.at(-1) ?? null;
}, { immediate: true });

watch(fullTimeline, () => {
  navigatorStart.value = 0;
  navigatorEnd.value = 100;
});

watch(effectiveNodeSeries, nodes => {
  const available = new Set(nodes.map(node => node.nodeId));
  const retained = new Set([...hiddenNodeIds.value].filter(nodeId => available.has(nodeId)));
  if (retained.size !== hiddenNodeIds.value.size) hiddenNodeIds.value = retained;
});

function compareNodes(left: NodeTrafficSeries, right: NodeTrafficSeries) {
  const byCode = naturalNodeOrder.compare(nodeCode(left), nodeCode(right));
  return byCode || left.nodeId - right.nodeId;
}

function compareBuckets(left: string, right: string) {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) return leftTime - rightTime;
  return left.localeCompare(right);
}

function normalizeBucket(value: string) {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : value;
}

function isCoveredNodePoint(point: NodeTrafficPoint) {
  if (!point.bucketStart) return false;
  if (props.coverage?.status !== 'PARTIAL' || !props.coverage.completeFrom) return true;
  const pointTime = Date.parse(point.bucketStart);
  const completeFrom = Date.parse(props.coverage.completeFrom);
  return !Number.isFinite(pointTime) || !Number.isFinite(completeFrom) || pointTime >= completeFrom;
}

function finiteNonNegative(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : null;
}

function totalPointBandwidth(point: TrafficPoint) {
  const serverValue = finiteNonNegative(point.bandwidthBps);
  if (serverValue !== null) return serverValue;
  const bytes = finiteNonNegative(point.bodyBytesSent);
  return bytes === null ? null : bytes * 8 / totalPointBucketSeconds(point);
}

function nodePointBandwidth(point: NodeTrafficPoint) {
  const serverValue = finiteNonNegative(point.bandwidthBps);
  if (serverValue !== null) return serverValue;
  const bytes = finiteNonNegative(point.bodyBytesSent);
  if (bytes === null || !Number.isFinite(point.bucketSeconds) || point.bucketSeconds <= 0) return null;
  return bytes * 8 / point.bucketSeconds;
}

function totalPointBucketSeconds(point: TrafficPoint) {
  const seconds = finiteNonNegative(point.bucketSeconds);
  return seconds !== null && seconds > 0 ? seconds : 3_600;
}

function nodeTotalAt(bucketIndex: number) {
  const bucket = timeline.value[bucketIndex];
  const values: number[] = [];
  let hasUnknown = props.coverage?.status === 'UNAVAILABLE';
  for (const points of allNodePointByBucket.value.values()) {
    const point = points.get(bucket);
    if (!point) continue;
    const value = nodePointBandwidth(point);
    if (value === null) hasUnknown = true;
    else values.push(value);
  }
  return {
    value: values.length ? values.reduce((total, value) => total + value, 0) : null,
    complete: values.length > 0 && !hasUnknown
  };
}

function nodeCode(node: NodeTrafficSeries) {
  const value = node.nodeCode?.trim();
  return value || '节点编号未记录';
}

function nodeName(node: NodeTrafficSeries) {
  const value = node.nodeName?.trim();
  return value || '节点名称未记录';
}

function nodeLegendLabel(node: NodeTrafficSeries) {
  const ip = node.nodeIp?.trim() || '入口 IP 未记录';
  return `${nodeCode(node)} · ${nodeName(node)} · ${ip}`;
}

function stableHash(value: string) {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function nodeStyle(nodeId: number): SeriesStyle {
  const hash = stableHash(String(nodeId));
  const colorIndex = hash % NODE_COLORS.length;
  return {
    color: NODE_COLORS[colorIndex],
    dash: nodeDashById.value.get(nodeId) ?? NODE_DASHES[0]
  };
}

function toggleNode(nodeId: number) {
  const next = new Set(hiddenNodeIds.value);
  if (next.has(nodeId)) next.delete(nodeId);
  else next.add(nodeId);
  hiddenNodeIds.value = next;
}

function plotWidth() {
  return CHART.width - CHART.left - CHART.right;
}

function plotHeight() {
  return CHART.height - CHART.top - CHART.bottom;
}

function xAt(index: number) {
  return CHART.left + (index / Math.max(1, timeline.value.length - 1)) * plotWidth();
}

function yAt(value: number) {
  return CHART.top + plotHeight() - (value / scaleMaxBps.value) * plotHeight();
}

function plotSegments(samples: PlotSample[], overview = false) {
  const buckets = overview ? fullTimeline.value : timeline.value;
  const bucketIndexes = overview ? new Map(buckets.map((bucket, index) => [bucket, index])) : timelineIndex.value;
  const segments: PlotPoint[][] = [];
  let segment: PlotPoint[] = [];
  const flush = () => {
    if (segment.length) segments.push(segment);
    segment = [];
  };

  for (const sample of samples) {
    if (sample.value === null) {
      flush();
      continue;
    }
    const index = bucketIndexes.get(sample.bucket);
    if (index === undefined) {
      flush();
      continue;
    }
    if (segment.length && !isNextCadencePoint(segment.at(-1) as PlotPoint, sample)) flush();
    segment.push({
      ...sample,
      x: overview ? index / Math.max(1, buckets.length - 1) * 1000 : xAt(index),
      y: overview ? 58 - sample.value / navigatorMaximum.value * 48 : yAt(sample.value)
    });
  }
  flush();
  return segments;
}

function smoothPointPath(points: Array<{ x: number; y: number }>) {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  let path = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const controlOffset = (current.x - previous.x) / 3;
    path += ` C ${(previous.x + controlOffset).toFixed(1)} ${previous.y.toFixed(1)} ${(current.x - controlOffset).toFixed(1)} ${current.y.toFixed(1)} ${current.x.toFixed(1)} ${current.y.toFixed(1)}`;
  }
  return path;
}

function isNextCadencePoint(previous: PlotSample, current: PlotSample) {
  const previousTime = Date.parse(previous.bucket);
  const currentTime = Date.parse(current.bucket);
  if (!Number.isFinite(previousTime) || !Number.isFinite(currentTime)) return false;
  if (!Number.isFinite(previous.cadenceSeconds) || previous.cadenceSeconds <= 0) return false;
  const elapsed = currentTime - previousTime;
  return elapsed > 0 && elapsed <= previous.cadenceSeconds * 1_000;
}

function linePath(samples: PlotSample[]) {
  return plotSegments(samples).map(segment => smoothPointPath(segment)).join(' ');
}

function singlePointMarkers(samples: PlotSample[]) {
  return plotSegments(samples).filter(segment => segment.length === 1).flat();
}

function nodeLinePath(nodeId: number) {
  return linePath(nodePlotSamples.value.get(nodeId) ?? []);
}

function nodeSinglePoints(nodeId: number) {
  return singlePointMarkers(nodePlotSamples.value.get(nodeId) ?? []);
}

function hitGeometry(index: number) {
  const center = xAt(index);
  const previous = index > 0 ? xAt(index - 1) : CHART.left;
  const next = index < timeline.value.length - 1 ? xAt(index + 1) : CHART.width - CHART.right;
  const left = index > 0 ? (previous + center) / 2 : CHART.left;
  const right = index < timeline.value.length - 1 ? (center + next) / 2 : CHART.width - CHART.right;
  return { x: left, width: Math.max(1, right - left) };
}

function selectBucket(bucket: string) {
  activeBucket.value = bucket;
}

function setNavigatorStart(event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  navigatorStart.value = Math.min(value, navigatorEnd.value - 5);
}

function setNavigatorEnd(event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  navigatorEnd.value = Math.max(value, navigatorStart.value + 5);
}

function niceCeiling(value: number) {
  if (!Number.isFinite(value) || value <= 1_000) return 1_000;
  const exponent = 10 ** Math.floor(Math.log10(value));
  const normalized = value / exponent;
  const step = [1, 2, 5, 10].find(candidate => normalized <= candidate) ?? 10;
  return step * exponent;
}

function formatBandwidth(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '暂无数据';
  const safe = Math.max(0, value);
  if (safe < 1_000_000) return `${formatCompact(safe / 1_000)} Kbps`;
  if (safe < 1_000_000_000) return `${formatCompact(safe / 1_000_000)} Mbps`;
  return `${formatCompact(safe / 1_000_000_000)} Gbps`;
}

function formatCompact(value: number) {
  if (value === 0) return '0';
  if (value >= 100) return Math.round(value).toLocaleString('zh-CN');
  if (value >= 10) return value.toFixed(1).replace(/\.0$/, '');
  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function bucketLabel(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const date = `${padTime(parsed.getMonth() + 1)}/${padTime(parsed.getDate())}`;
  const time = `${padTime(parsed.getHours())}:${padTime(parsed.getMinutes())}`;
  if (usesIntradayScale()) return time;
  return props.granularity === '1h' ? `${date} ${time}` : date;
}

function usesIntradayScale() {
  const timestamps = fullTimeline.value.map(bucket => Date.parse(bucket)).filter(Number.isFinite);
  if (timestamps.length >= 2) return Math.max(...timestamps) - Math.min(...timestamps) <= 86_400_000;
  return props.granularity === '5m' || ['today', '1h', '6h', '24h'].includes(props.timeRange);
}

function padTime(value: number) {
  return String(value).padStart(2, '0');
}
</script>

<template>
  <section class="bandwidth-trend-chart" aria-label="总带宽与节点带宽趋势">
    <p v-if="coverageText" class="coverage-notice" role="status">{{ coverageText }}</p>

    <div v-if="timeline.length" class="chart-stage">
      <button type="button" class="time-pan time-pan-left" aria-label="查看上一时间窗口" @click="emit('pan', -1)">‹</button>
      <button type="button" class="time-pan time-pan-right" aria-label="查看下一时间窗口" @click="emit('pan', 1)">›</button>
      <svg
        class="bandwidth-chart"
        :data-scale-max-bps="scaleMaxBps"
        :viewBox="`0 0 ${CHART.width} ${CHART.height}`"
        preserveAspectRatio="none"
        role="img"
        aria-label="带宽趋势图"
      >
        <g class="grid-lines" aria-hidden="true">
          <line
            v-for="tick in yAxisTicks"
            :key="tick.ratio"
            :x1="CHART.left"
            :x2="CHART.width - CHART.right"
            :y1="tick.y"
            :y2="tick.y"
          />
          <line
            v-for="tick in xAxisTicks"
            :key="`vertical-${tick.bucket}`"
            :x1="tick.x"
            :x2="tick.x"
            :y1="CHART.top"
            :y2="CHART.height - CHART.bottom"
          />
        </g>
        <path
          v-for="node in visibleNodeSeries"
          :key="node.nodeId"
          class="series-line node-line"
          data-series="node"
          :data-node-id="node.nodeId"
          :d="nodeLinePath(node.nodeId)"
          :stroke="nodeStyle(node.nodeId).color"
          :stroke-dasharray="nodeStyle(node.nodeId).dash || undefined"
          stroke-width="1.75"
          aria-hidden="true"
        />
        <g v-for="node in visibleNodeSeries" :key="`points-${node.nodeId}`" aria-hidden="true">
          <circle
            v-for="point in nodeSinglePoints(node.nodeId)"
            :key="point.bucket"
            class="series-single-point node-single-point"
            :data-node-id="node.nodeId"
            :cx="point.x"
            :cy="point.y"
            r="4"
            :fill="nodeStyle(node.nodeId).color"
          />
        </g>
        <line
          v-if="activeGuideX !== null"
          class="selected-bucket-guide"
          :x1="activeGuideX"
          :x2="activeGuideX"
          :y1="CHART.top"
          :y2="CHART.height - CHART.bottom"
          aria-hidden="true"
        />
        <g class="bucket-hits">
          <rect
            v-for="(bucket, index) in timeline"
            :key="bucket"
            class="bucket-hit"
            :class="{ active: activeBucket === bucket }"
            :data-bucket="bucket"
            :x="hitGeometry(index).x"
            :width="hitGeometry(index).width"
            :y="CHART.top"
            :height="CHART.height - CHART.top - CHART.bottom"
            tabindex="0"
            role="button"
            :aria-label="`查看 ${bucketLabel(bucket)} 的总带宽与节点带宽`"
            @mouseenter="selectBucket(bucket)"
            @focus="selectBucket(bucket)"
            @click="selectBucket(bucket)"
          />
        </g>
      </svg>
      <div class="y-axis" aria-hidden="true">
        <span v-for="tick in yAxisTicks" :key="tick.ratio" :style="{ top: `${(tick.y / CHART.height) * 100}%` }">
          {{ tick.label }}
        </span>
      </div>
      <div class="x-axis" aria-hidden="true">
        <span
          v-for="tick in xAxisTicks"
          :key="tick.bucket"
          :style="{ left: `${(tick.x / CHART.width) * 100}%`, transform: tick.transform }"
        >
          {{ tick.label }}
        </span>
      </div>
      <div
        v-if="activeBucket && activeGuideX !== null"
        class="floating-tooltip"
        :style="activeTooltipStyle"
      >
        <strong>{{ bucketLabel(activeBucket) }}</strong>
        <span v-for="row in activeDetailRows" :key="row.key" :data-detail-key="row.key">
          <svg v-if="row.key !== 'total'" class="series-swatch detail-swatch" viewBox="0 0 48 12" aria-hidden="true">
            <line x1="2" y1="6" x2="46" y2="6" :stroke="row.color" :stroke-dasharray="row.dash || undefined" />
          </svg>
          {{ row.label }} <b>{{ formatBandwidth(row.value) }}</b>
        </span>
      </div>
    </div>
    <div v-else class="chart-empty">
      <strong>带宽统计处理中</strong>
      <p>日志时间桶写入后会显示总带宽数值和节点曲线。</p>
    </div>

    <div v-if="fullTimeline.length" class="timeline-navigator">
      <button type="button" aria-label="向前移动时间" @click="emit('pan', -1)">‹</button>
      <div class="navigator-track">
        <svg viewBox="0 0 1000 64" preserveAspectRatio="none" aria-hidden="true">
          <path v-for="node in visibleNodeSeries" :key="node.nodeId"
            class="navigator-node-line" :data-node-id="node.nodeId"
            :d="navigatorNodePath(node.nodeId)" :stroke="nodeStyle(node.nodeId).color"
            :stroke-dasharray="nodeStyle(node.nodeId).dash" />
        </svg>
        <span class="navigator-selection" :style="navigatorSelectionStyle" />
        <input class="navigator-range navigator-start" type="range" min="0" max="95" :value="navigatorStart" aria-label="可见时间起点" @input="setNavigatorStart" />
        <input class="navigator-range navigator-end" type="range" min="5" max="100" :value="navigatorEnd" aria-label="可见时间终点" @input="setNavigatorEnd" />
      </div>
      <button type="button" aria-label="向后移动时间" @click="emit('pan', 1)">›</button>
      <div class="navigator-ticks" aria-hidden="true">
        <span v-for="tick in navigatorTicks" :key="tick">{{ bucketLabel(tick) }}</span>
      </div>
    </div>

    <div v-if="timeline.length" class="chart-footer">
      <div class="chart-legend" role="list" aria-label="带宽曲线图例">
        <span class="legend-item total-legend-item" role="listitem">
          <span class="legend-label">总带宽</span>
          <strong>{{ formatBandwidth(currentPoint?.value) }}</strong>
        </span>
        <button
          v-for="node in rankedNodeSeries"
          :key="node.nodeId"
          type="button"
          class="legend-item node-legend-toggle"
          :data-legend-node-id="node.nodeId"
          :aria-pressed="!hiddenNodeIds.has(node.nodeId)"
          :aria-label="`${hiddenNodeIds.has(node.nodeId) ? '显示' : '隐藏'} ${nodeLegendLabel(node)}`"
          @click="toggleNode(node.nodeId)"
        >
          <svg class="series-swatch" viewBox="0 0 48 12" aria-hidden="true">
            <line x1="2" y1="6" x2="46" y2="6" :stroke="nodeStyle(node.nodeId).color" :stroke-dasharray="nodeStyle(node.nodeId).dash" />
          </svg>
          <span class="node-legend-text">
            <span class="legend-label">{{ nodeCode(node) }} · {{ nodeName(node) }}</span>
            <span class="node-legend-ip">{{ node.nodeIp?.trim() || 'IP 未记录' }}</span>
          </span>
        </button>
      </div>
      <div class="chart-insights">
        <div class="peak-insight"><span>峰值</span><strong>{{ formatBandwidth(peakPoint?.value) }}</strong><small>{{ peakPoint ? bucketLabel(peakPoint.bucket) : '暂无时间' }}</small></div>
        <div><span>当前时间</span><strong>{{ currentPoint ? bucketLabel(currentPoint.bucket) : '暂无数据' }}</strong></div>
        <div><span>当前值</span><strong>{{ formatBandwidth(currentPoint?.value) }}</strong></div>
        <button v-if="!realtime" type="button" @click="emit('return-realtime')">▶ 回到实时</button>
      </div>
    </div>

    <div v-if="activeBucket" class="bucket-detail" aria-live="polite">
      <strong class="bucket-detail-time">{{ bucketLabel(activeBucket) }}</strong>
      <p
        v-if="activeReconciliation"
        class="bucket-reconciliation"
        :data-state="activeReconciliation.state"
      >{{ activeReconciliation.text }}</p>
      <div class="bucket-detail-rows">
        <div v-for="row in activeDetailRows" :key="row.key" class="bucket-detail-row">
          <span>
            <svg v-if="row.key !== 'total'" class="series-swatch detail-swatch" viewBox="0 0 48 12" aria-hidden="true">
              <line x1="2" y1="6" x2="46" y2="6" :stroke="row.color" :stroke-dasharray="row.dash || undefined" />
            </svg>{{ row.label }}
          </span>
          <strong>{{ formatBandwidth(row.value) }}</strong>
        </div>
      </div>
    </div>
    <p v-else-if="timeline.length" class="detail-hint">悬停、轻触或使用键盘聚焦时间桶，可查看同一时刻的总量与节点明细。</p>
  </section>
</template>

<style scoped>
.bandwidth-trend-chart {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  color: #142b21;
}

.coverage-notice {
  margin: 0 0 12px;
  border: 1px solid #e8d49c;
  border-radius: 12px;
  padding: 10px 12px;
  color: #765513;
  background: #fff9e9;
  font-size: 13px;
  line-height: 1.55;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
  min-width: 0;
  gap: 8px;
  flex: 1 1 760px;
}

.legend-item {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  border: 1px solid #d5e4dc;
  border-radius: 12px;
  padding: 10px 12px;
  min-height: 48px;
  box-sizing: border-box;
  color: #405c4d;
  background: #fff;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.4;
}

.total-legend-item {
  border-color: #bdddc9;
  background: #edf8f1;
  flex-wrap: wrap;
}

.node-legend-toggle {
  min-height: 48px;
  max-width: 100%;
  min-width: 0;
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.node-legend-toggle:hover,
.node-legend-toggle:focus-visible {
  border-color: #7ea990;
  outline: 3px solid rgba(18, 115, 78, 0.12);
}

.node-legend-toggle[aria-pressed="false"] {
  color: #829087;
  background: #f5f5f3;
  opacity: 0.75;
}

.legend-label {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-legend-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; max-width: 180px; }
.node-legend-ip { font-size: 12px; line-height: 1.4; color: inherit; opacity: .8; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }

.legend-item strong { color: #153d2b; font-size: 16px; font-variant-numeric: tabular-nums; }
/* One SVG dash pattern is shared by chart, chip and detail samples. */
.series-swatch { width: 48px; height: 12px; flex: 0 0 48px; overflow: visible; }
.series-swatch line { fill: none; stroke-width: 2.5; stroke-linecap: round; }
.detail-swatch { width: 40px; flex-basis: 40px; }
.node-legend-toggle[aria-pressed="false"] .legend-label { text-decoration: line-through; }

.chart-stage {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 318px;
  overflow: hidden;
  border: 1px solid rgba(24, 70, 49, 0.12);
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(247, 251, 248, 0.96), rgba(255, 255, 255, 0.98)),
    repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(20, 68, 45, 0.03) 80px);
}

.time-pan {
  position: absolute;
  z-index: 4;
  top: 42%;
  width: 38px;
  height: 38px;
  border: 1px solid #c9ddd1;
  border-radius: 50%;
  color: #0f6948;
  background: rgba(248, 252, 249, 0.94);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}
.time-pan-left { left: 8px; }
.time-pan-right { right: 8px; }


.floating-tooltip {
  position: absolute;
  z-index: 5;
  display: grid;
  gap: 5px;
  width: 300px;
  transform: translate(-50%, -55%);
  border: 1px solid #c8dbcf;
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 30px rgba(15, 74, 49, 0.14);
  font-size: 12px;
  pointer-events: none;
}
.floating-tooltip span { display: flex; align-items: center; gap: 6px; }
.floating-tooltip { max-width: calc(100% - 20px); box-sizing: border-box; }
.floating-tooltip span { overflow-wrap: anywhere; }
.floating-tooltip b { flex-shrink: 0; white-space: nowrap; }
.floating-tooltip b { margin-left: auto; }

.timeline-navigator {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 38px;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
}
.navigator-ticks { grid-column: 2; display: flex; justify-content: space-between; margin-top: -5px; color: #7a8b82; font-size: 10px; font-variant-numeric: tabular-nums; }
.timeline-navigator > button {
  height: 38px;
  border: 1px solid #c9ddd1;
  border-radius: 10px;
  color: #0f6948;
  background: #f7fbf8;
  font-size: 24px;
  cursor: pointer;
}
.navigator-track { position: relative; height: 54px; overflow: hidden; border: 1px solid #dbe8df; border-radius: 8px; background: linear-gradient(180deg,#f4f8f5,#eaf1ed); }
.navigator-track svg { position: absolute; inset: 0; width: 100%; height: 100%; }
.navigator-track path { fill: none; stroke-width: 2; vector-effect: non-scaling-stroke; }
.navigator-selection { position: absolute; inset-block: 0; border: 1px solid #15845a; background: rgba(21, 132, 90, 0.08); pointer-events: none; }
.navigator-range { position: absolute; inset: 0; width: 100%; margin: 0; appearance: none; background: transparent; pointer-events: none; }
.navigator-range::-webkit-slider-thumb { width: 14px; height: 38px; appearance: none; border: 1px solid #b9d3c4; border-radius: 5px; background: repeating-linear-gradient(90deg,#fff 0 4px,#8eb7a1 4px 5px,#fff 5px 8px); box-shadow: 0 2px 6px rgba(15,76,49,.18); pointer-events: auto; cursor: ew-resize; }

.chart-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 10px;
  border-top: 1px solid #e0e9e3;
  padding: 11px 4px 0;
}
.chart-insights {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.chart-insights > div { display: grid; min-width: 130px; gap: 3px; border-right: 1px solid #d6e2da; padding: 4px 18px 4px 0; }
.chart-insights span, .chart-insights small { color: #6b7d73; font-size: 11px; }
.chart-insights strong { font-size: 18px; }
.chart-insights .peak-insight strong { color: #b66d00; }
.chart-insights button { margin-left: auto; border: 1px solid #b9d5c5; border-radius: 10px; padding: 0 16px; color: #0d6746; background: #eef8f1; font-weight: 800; cursor: pointer; }

.bandwidth-chart {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 278px;
}

.grid-lines line {
  stroke: rgba(30, 78, 55, 0.1);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.series-line {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}


.series-single-point {
  stroke: #fff;
  stroke-width: 1.5;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
}

.bucket-hit {
  fill: transparent;
  stroke: transparent;
  vector-effect: non-scaling-stroke;
  cursor: crosshair;
}

.bucket-hit:hover,
.bucket-hit:focus,
.bucket-hit.active {
  fill: transparent;
  stroke: transparent;
  outline: none;
}

.selected-bucket-guide {
  stroke: #12734e;
  stroke-width: 1.5;
  stroke-dasharray: 4 3;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
  filter: drop-shadow(0 0 3px rgba(18, 115, 78, 0.28));
}

.y-axis span,
.x-axis span {
  position: absolute;
  color: #718279;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.y-axis span {
  left: 12px;
  transform: translateY(-50%);
}

.x-axis span {
  bottom: 12px;
  transform: translateX(-50%);
  white-space: nowrap;
}

.bucket-detail {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.bucket-detail-time {
  color: #173d2b;
}

.bucket-reconciliation {
  margin: 0;
  color: #64756c;
  font-size: 12px;
  font-weight: 700;
}

.bucket-reconciliation[data-state="match"] {
  color: #12734e;
}

.bucket-reconciliation[data-state="mismatch"] {
  color: #b42318;
}

.bucket-detail-rows {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 190px), 1fr));
  gap: 8px 16px;
}

.bucket-detail-row {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #526a5d;
  font-size: 12px;
}

.bucket-detail-row span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
  overflow-wrap: anywhere;
}

.bucket-detail-row strong {
  flex: 0 0 auto;
  color: #173d2b;
  font-variant-numeric: tabular-nums;
}

.detail-hint,
.chart-empty {
  color: #64756c;
  font-size: 13px;
}

.detail-hint {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.chart-empty {
  display: grid;
  min-height: 220px;
  place-content: center;
  border: 1px solid rgba(24, 70, 49, 0.12);
  border-radius: 18px;
  text-align: center;
  background: #f8fbf8;
}

.chart-empty p {
  margin: 8px 0 0;
}

@media (max-width: 480px) {
  .chart-legend { flex-basis: auto; }
  .total-legend-item { width: 100%; }
  .legend-label { max-width: none; white-space: normal; }
  .node-legend-toggle {
    width: 100%;
    flex: 1 1 100%;
  }

  .chart-stage {
    min-height: 288px;
  }

  .bandwidth-chart {
    height: 250px;
  }

  .bucket-detail-row {
    align-items: flex-start;
  }

  .chart-footer { align-items: stretch; flex-direction: column; }
  .chart-insights { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
